/**
 * Animated SVG of the overall Ecospora circular system — the platonic loop
 * the brand is built around. Same visual language as CircularLoop, different job:
 *
 *   CircularLoop = one initiative (LKTN x asnaf community loop)  → Impact page
 *   SystemLoop   = the whole operating principle                  → Home page
 *
 * Six nodes carry the Pillar 4 thesis end to end:
 *   Kenaf → Beglog → Mushroom → Spent Substrate → Compost → Soil
 */
const NODES = [
  { label: "Kenaf",            sub: "Sustainable substrate" },
  { label: "Beglog",           sub: "Substrate block" },
  { label: "Mushroom",         sub: "Harvest" },
  { label: "Spent Substrate",  sub: "After harvest" },
  { label: "Compost",          sub: "Soil nutrient" },
  { label: "Soil",             sub: "Returns to earth" },
];

const SystemLoop = ({ className = "" }) => {
  const cx = 250;
  const cy = 250;
  const r = 170;

  return (
    <div
      className={`relative w-full max-w-[560px] aspect-square mx-auto ${className}`}
      data-testid="system-loop"
    >
      <svg viewBox="0 0 500 500" className="w-full h-full" aria-hidden="true">
        <defs>
          <marker
            id="system-arrow"
            viewBox="0 0 10 10"
            refX="5"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto-start-reverse"
          >
            <path d="M0,0 L10,5 L0,10 z" fill="#2C4A3E" />
          </marker>
        </defs>

        {/* Outer rotating dashed ring */}
        <g
          className="loop-rotate"
          style={{ transformBox: "fill-box", transformOrigin: "center" }}
        >
          <circle
            cx={cx}
            cy={cy}
            r={r + 28}
            fill="none"
            stroke="#C9A961"
            strokeWidth="1"
            strokeDasharray="2 8"
            opacity="0.55"
          />
        </g>

        {/* Static inner ring */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="#2C4A3E"
          strokeWidth="1.25"
          opacity="0.4"
        />

        {/* Centre */}
        <circle cx={cx} cy={cy} r="64" fill="#F5F1E8" stroke="#2C4A3E" strokeWidth="1" />
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          fontFamily="Fraunces, serif"
          fontSize="22"
          fontWeight="600"
          fill="#2C4A3E"
        >
          Ecospora
        </text>
        <text
          x={cx}
          y={cy + 18}
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          fontSize="9"
          letterSpacing="2"
          fill="#6B6B65"
        >
          THE SYSTEM
        </text>

        {/* Nodes + connecting arcs */}
        {NODES.map((node, i) => {
          const angle = (i / NODES.length) * Math.PI * 2 - Math.PI / 2;
          const x = cx + Math.cos(angle) * r;
          const y = cy + Math.sin(angle) * r;
          const labelOffset = 20;
          const lx = cx + Math.cos(angle) * (r + labelOffset);
          const ly = cy + Math.sin(angle) * (r + labelOffset);
          const align =
            Math.abs(Math.cos(angle)) < 0.2
              ? "middle"
              : Math.cos(angle) > 0
              ? "start"
              : "end";

          const nextAngle = ((i + 1) / NODES.length) * Math.PI * 2 - Math.PI / 2;
          const nx = cx + Math.cos(nextAngle) * r;
          const ny = cy + Math.sin(nextAngle) * r;

          return (
            <g key={node.label}>
              <path
                d={`M ${x} ${y} A ${r} ${r} 0 0 1 ${nx} ${ny}`}
                fill="none"
                stroke="#2C4A3E"
                strokeWidth="1.5"
                opacity="0.85"
              />
              <circle cx={x} cy={y} r="9" fill="#F5F1E8" stroke="#2C4A3E" strokeWidth="1.5" />
              <circle cx={x} cy={y} r="3" fill="#C9A961" />
              <text
                x={lx}
                y={ly - 2}
                textAnchor={align}
                fontFamily="Fraunces, serif"
                fontSize="14"
                fontWeight="600"
                fill="#2C4A3E"
              >
                {String(i + 1).padStart(2, "0")} · {node.label}
              </text>
              <text
                x={lx}
                y={ly + 14}
                textAnchor={align}
                fontFamily="Inter, sans-serif"
                fontSize="10"
                fill="#6B6B65"
              >
                {node.sub}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Screen-reader summary of the loop */}
      <p className="sr-only">
        Ecospora's circular system: kenaf grows the beglog. The beglog grows the mushroom. The spent substrate becomes compost. The compost returns to the soil. The soil grows the kenaf.
      </p>
    </div>
  );
};

export default SystemLoop;
